var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css, nothing } from 'lit';
import { property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import '../../button/button.js';
/**
 * A date range picker component for selecting date ranges with relative and absolute options.
 *
 * @fires change - Fired when the date range selection changes
 * @csspart trigger - The trigger button element
 * @csspart dropdown - The dropdown container
 */
export class KRDateRangePicker extends LitElement {
    constructor() {
        super();
        /**
         * The input name for form submission / <kr-form> change collection.
         */
        this.name = '';
        /**
         * Whether the user has changed this field since it was last loaded or marked pristine. Set when
         * the user picks a range, cleared by markPristine(). Used by <kr-form> to collect changed fields.
         */
        this.dirty = false;
        /**
         * Whether a <kr-form> save carrying this field's edit is in flight. Set by markSaving(), cleared
         * by markSaved()/markSaveFailed().
         */
        this.saving = false;
        /**
         * Relative date options to display
         */
        this.relativeOptions = [
            {
                key: 'last-5-minutes',
                amount: 5,
                unit: 'minute',
                type: 'relative'
            },
            {
                key: 'last-30-minutes',
                amount: 30,
                unit: 'minute',
                type: 'relative'
            },
            {
                key: 'last-1-hour',
                amount: 1,
                unit: 'hour',
                type: 'relative'
            },
            {
                key: 'last-6-hours',
                amount: 6,
                unit: 'hour',
                type: 'relative'
            },
            {
                key: 'last-1-day',
                amount: 1,
                unit: 'day',
                type: 'relative'
            },
            {
                key: 'last-7-days',
                amount: 7,
                unit: 'day',
                type: 'relative'
            },
            {
                key: 'last-30-days',
                amount: 30,
                unit: 'day',
                type: 'relative'
            },
        ];
        /**
         * Whether the picker is disabled
         */
        this.disabled = false;
        /**
         * Whether the picker is in an invalid state
         */
        this.invalid = false;
        /**
         * Placeholder text when no range is selected
         */
        this.placeholder = 'Select a date range';
        /**
         * Initial start date for absolute mode (YYYY-MM-DD)
         */
        this.startDate = '';
        /**
         * Initial end date for absolute mode (YYYY-MM-DD)
         */
        this.endDate = '';
        this._isOpen = false;
        this._activeTab = 'relative';
        this._tempStartDate = '';
        this._tempEndDate = '';
        this._handleClickOutside = (e) => {
            if (!e.composedPath().includes(this)) {
                this._isOpen = false;
            }
        };
        this._internals = this.attachInternals();
    }
    connectedCallback() {
        super.connectedCallback();
        // Marks this as a krubble form field so <kr-form> can find its controls (.kr-form-field).
        this.classList.add('kr-form-field');
        document.addEventListener('click', this._handleClickOutside);
        if (this.startDate) {
            this._tempStartDate = this.startDate;
        }
        if (this.endDate) {
            this._tempEndDate = this.endDate;
        }
        // Set initial active tab based on mode
        if (this.mode === 'relative') {
            this._activeTab = 'relative';
        }
        else if (this.mode === 'absolute') {
            this._activeTab = 'absolute';
        }
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('click', this._handleClickOutside);
    }
    willUpdate(changedProperties) {
        // This field's own save-mode, else the enclosing <kr-form>'s. Unset either way means manual.
        let saveMode = this.saveMode;
        if (!saveMode) {
            saveMode = this.closest('kr-form')?.saveMode;
        }
        // While the user has an unsaved edit, an external `value` set (e.g. a parent re-binding from a
        // server response that predates the edit) would overwrite it. Revert to the user's value. The
        // field accepts external values again once the edit is saved (markSaving clears dirty) or reset.
        if (changedProperties.has('value') && this.dirty && this.value !== this._dirtyValue && saveMode === 'autosave') {
            this.value = this._dirtyValue;
        }
    }
    formResetCallback() {
        this.value = undefined;
        this.startDate = '';
        this.endDate = '';
        this._tempStartDate = '';
        this._tempEndDate = '';
        this.dirty = false;
        this.saving = false;
        this._internals.setFormValue(null);
    }
    // Clears the dirty flag so the field is no longer reported as changed.
    markPristine() {
        this.dirty = false;
    }
    // <kr-form> calls this when a save carrying this field's edit starts. Clearing dirty here means a
    // further edit during the request is tracked as new and goes out in the next save.
    markSaving() {
        this.saving = true;
        this.dirty = false;
    }
    // <kr-form> calls this when the save succeeded. If the user edited mid-flight the field is dirty
    // again and stays that way.
    markSaved() {
        this.saving = false;
    }
    // <kr-form> calls this when the save failed. Restore dirty so the next change retries the edit.
    markSaveFailed() {
        this.saving = false;
        this.dirty = true;
        this._dirtyValue = this.value;
    }
    // A range has no single scalar value, so submit it as FormData with multiple named entries — the
    // standard way a form-associated element mirrors several native inputs. Keys use dot notation
    // (`${name}.startDate`) so a bracket/dot-parsing backend reconstructs the exact
    // KRDateRangePickerValue object. Absolute ranges submit `.startDate`/`.endDate`; relative ranges
    // submit `.amount`/`.unit`.
    _updateFormValue() {
        if (!this.value || !this.name) {
            this._internals.setFormValue(null);
            return;
        }
        const formData = new FormData();
        formData.append(`${this.name}.type`, this.value.type);
        if (this.value.type === 'absolute') {
            formData.append(`${this.name}.startDate`, this.value.startDate || '');
            formData.append(`${this.name}.endDate`, this.value.endDate || '');
        }
        else {
            formData.append(`${this.name}.amount`, String(this.value.amount ?? ''));
            formData.append(`${this.name}.unit`, this.value.unit || '');
        }
        this._internals.setFormValue(formData);
    }
    _handleTriggerClick() {
        if (this.disabled) {
            return;
        }
        this._isOpen = !this._isOpen;
    }
    _handleTabClick(tab) {
        this._activeTab = tab;
    }
    _handleRelativeSelect(option) {
        this.value = {
            type: 'relative',
            amount: option.amount,
            unit: option.unit,
        };
        this.dirty = true;
        this._dirtyValue = this.value;
        this._updateFormValue();
        this._isOpen = false;
        this.dispatchEvent(new CustomEvent('change', {
            detail: { value: this.value },
            bubbles: true,
            composed: true,
        }));
    }
    _handleApplyAbsolute() {
        if (!this._tempStartDate) {
            return;
        }
        if (!this._tempEndDate) {
            return;
        }
        if (this._tempStartDate >= this._tempEndDate) {
            return;
        }
        this.value = {
            type: 'absolute',
            startDate: this._tempStartDate,
            endDate: this._tempEndDate,
        };
        this.startDate = this._tempStartDate;
        this.endDate = this._tempEndDate;
        this.dirty = true;
        this._dirtyValue = this.value;
        this._updateFormValue();
        this._isOpen = false;
        this.dispatchEvent(new CustomEvent('change', {
            detail: { value: this.value },
            bubbles: true,
            composed: true,
        }));
    }
    _handleCancel() {
        this._isOpen = false;
        this._tempStartDate = this.startDate;
        this._tempEndDate = this.endDate;
    }
    _formatRelativeOption(option) {
        if (option.label) {
            return option.label;
        }
        if (option.amount === 0) {
            if (option.unit === 'day') {
                return 'Today';
            }
            return `This ${option.unit}`;
        }
        const unitLabels = {};
        if (option.amount === 1) {
            unitLabels['second'] = 'second';
            unitLabels['minute'] = 'minute';
            unitLabels['hour'] = 'hour';
            unitLabels['day'] = 'day';
            unitLabels['week'] = 'week';
            unitLabels['month'] = 'month';
            unitLabels['year'] = 'year';
        }
        else {
            unitLabels['second'] = 'seconds';
            unitLabels['minute'] = 'minutes';
            unitLabels['hour'] = 'hours';
            unitLabels['day'] = 'days';
            unitLabels['week'] = 'weeks';
            unitLabels['month'] = 'months';
            unitLabels['year'] = 'years';
        }
        return `Last ${option.amount} ${unitLabels[option.unit]}`;
    }
    _renderTriggerText() {
        // A relative range reads as its "Last N units" label; an absolute one as the
        // date pair. Anything incomplete falls through to the placeholder.
        let displayValue = '';
        if (this.value) {
            if (this.value.type === 'relative' && this.value.amount !== undefined && this.value.unit) {
                displayValue = this._formatRelativeOption({
                    key: '',
                    amount: this.value.amount,
                    unit: this.value.unit,
                    type: 'relative',
                });
            }
            if (this.value.type === 'absolute' && this.value.startDate && this.value.endDate) {
                displayValue = `${this.value.startDate} - ${this.value.endDate}`;
            }
        }
        if (displayValue) {
            return html `${displayValue}`;
        }
        return html `<span class="trigger-placeholder">${this.placeholder}</span>`;
    }
    _renderContent() {
        if (this.mode === 'relative') {
            return this._renderRelativeContent();
        }
        if (this.mode === 'absolute') {
            return this._renderAbsoluteContent();
        }
        if (this._activeTab === 'relative') {
            return this._renderRelativeContent();
        }
        return this._renderAbsoluteContent();
    }
    _renderRelativeContent() {
        return html `
      <div class="relative-options">
        ${this.relativeOptions.map((option) => html `
            <button
              class=${classMap({
            'relative-option': true,
            'relative-option--selected': this.value?.type === 'relative' &&
                this.value?.amount === option.amount &&
                this.value?.unit === option.unit
        })}
              type="button"
              @click=${() => this._handleRelativeSelect(option)}
            >
              ${this._formatRelativeOption(option)}
            </button>
          `)}
      </div>
    `;
    }
    _renderAbsoluteContent() {
        return html `
      <div class="absolute">
        <div class="date-row">
          <span class="date-label">Start</span>
          <input
            class="date-input"
            type="date"
            .value=${this._tempStartDate}
            @change=${this._handleStartDateChange}
          />
        </div>
        <div class="date-row">
          <span class="date-label">End</span>
          <input
            class="date-input"
            type="date"
            .value=${this._tempEndDate}
            @change=${this._handleEndDateChange}
          />
        </div>
      </div>
    `;
    }
    _renderFooter() {
        if (this.mode === 'absolute') {
            return html `
        <div class="footer">
          <kr-button
            variant="outline"
            size="small"
            @click=${this._handleCancel}
          >
            Cancel
          </kr-button>
          <kr-button
            variant="primary"
            size="small"
            ?disabled=${!this._tempStartDate || !this._tempEndDate}
            @click=${this._handleApplyAbsolute}
          >
            Apply
          </kr-button>
        </div>
      `;
        }
        if (this.mode === 'relative') {
            return nothing;
        }
        if (this._activeTab === 'absolute') {
            return html `
        <div class="footer">
          <kr-button
            variant="outline"
            size="small"
            @click=${this._handleCancel}
          >
            Cancel
          </kr-button>
          <kr-button
            variant="primary"
            size="small"
            ?disabled=${!this._tempStartDate || !this._tempEndDate}
            @click=${this._handleApplyAbsolute}
          >
            Apply
          </kr-button>
        </div>
      `;
        }
        return nothing;
    }
    _handleStartDateChange(e) {
        this._tempStartDate = e.target.value;
    }
    _handleEndDateChange(e) {
        this._tempEndDate = e.target.value;
    }
    render() {
        return html `
      <button
        part="trigger"
        class=${classMap({
            'trigger': true,
            'trigger--invalid': this.invalid
        })}
        type="button"
        ?disabled=${this.disabled}
        @click=${this._handleTriggerClick}
      >
        <svg
          class="icon"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
        <span class="trigger-text">
          ${this._renderTriggerText()}
        </span>
        <svg
          class="icon"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      <div part="dropdown" class=${classMap({
            'dropdown': true,
            'dropdown--open': this._isOpen
        })}>
        ${!this.mode ? html `
          <div class="tabs">
            <button
              class=${classMap({
            'tab': true,
            'tab--active': this._activeTab === 'relative'
        })}
              type="button"
              @click=${() => this._handleTabClick('relative')}
            >
              Relative
            </button>
            <button
              class=${classMap({
            'tab': true,
            'tab--active': this._activeTab === 'absolute'
        })}
              type="button"
              @click=${() => this._handleTabClick('absolute')}
            >
              Absolute
            </button>
          </div>
        ` : nothing}

        <div class="content">
          ${this._renderContent()}
        </div>

        ${this._renderFooter()}
      </div>
    `;
    }
}
KRDateRangePicker.styles = css `
    :host {
      display: inline-block;
      position: relative;
      font-family: inherit;
    }

    .trigger {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      min-width: 250px;
      padding: 0.5rem 0.75rem;
      font-size: 14px;
      font-family: inherit;
      color: #111827;
      background-color: #fff;
      border: 1px solid #d1d5db;
      border-radius: 4px;
      cursor: pointer;
      transition: border-color 0.2s, box-shadow 0.2s;
      text-align: left;
    }

    .trigger:hover:not(:disabled) {
      border-color: #9ca3af;
    }

    .trigger:focus {
      outline: none;
      border-color: #2563eb;
      box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
    }

    .trigger:disabled {
      background-color: #f3f4f6;
      color: #9ca3af;
      cursor: not-allowed;
    }

    .trigger--invalid {
      border-color: #ef4444;
    }

    .trigger-text {
      flex: 1;
    }

    .trigger-placeholder {
      color: #9ca3af;
    }

    .icon {
      width: 16px;
      height: 16px;
      color: #6b7280;
      flex-shrink: 0;
    }

    .dropdown {
      position: absolute;
      top: calc(100% + 4px);
      left: 0;
      z-index: 1000;
      min-width: 320px;
      background-color: #fff;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
      display: none;
    }

    .dropdown--open {
      display: block;
    }

    .tabs {
      display: flex;
      border-bottom: 1px solid #e5e7eb;
    }

    .tab {
      flex: 1;
      padding: 0.75rem;
      font-size: 14px;
      font-weight: 500;
      font-family: inherit;
      color: #6b7280;
      background: none;
      border: none;
      border-bottom: 2px solid transparent;
      cursor: pointer;
      transition: color 0.2s;
    }

    .tab:hover {
      color: #111827;
    }

    .tab--active {
      color: #2563eb;
      border-bottom-color: #2563eb;
    }

    .content {
      padding: 1rem;
    }

    .relative-options {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }

    .relative-option {
      display: flex;
      align-items: center;
      padding: 0.5rem 0.75rem;
      font-size: 14px;
      font-family: inherit;
      color: #111827;
      background: none;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      text-align: left;
      transition: background-color 0.2s;
    }

    .relative-option:hover {
      background-color: #f3f4f6;
    }

    .relative-option--selected {
      background-color: #eff6ff;
      color: #1e40af;
    }

    .absolute {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    .date-row {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }

    .date-label {
      width: 60px;
      font-size: 14px;
      color: #6b7280;
    }

    .date-input {
      flex: 1;
      padding: 0.5rem 0.75rem;
      font-size: 14px;
      font-family: inherit;
      border: 1px solid #d1d5db;
      border-radius: 4px;
    }

    .date-input:focus {
      outline: none;
      border-color: #2563eb;
      box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
    }

    .footer {
      display: flex;
      justify-content: flex-end;
      gap: 0.5rem;
      padding: 0.75rem 1rem;
      border-top: 1px solid #e5e7eb;
    }
  `;
// Enable form association
KRDateRangePicker.formAssociated = true;
__decorate([
    property({ type: String })
], KRDateRangePicker.prototype, "name", void 0);
__decorate([
    property({
        type: Boolean,
        reflect: true,
    })
], KRDateRangePicker.prototype, "dirty", void 0);
__decorate([
    property({ attribute: 'save-mode' })
], KRDateRangePicker.prototype, "saveMode", void 0);
__decorate([
    property({
        type: Boolean,
        reflect: true,
    })
], KRDateRangePicker.prototype, "saving", void 0);
__decorate([
    property({ type: Object })
], KRDateRangePicker.prototype, "value", void 0);
__decorate([
    property({ type: Array })
], KRDateRangePicker.prototype, "relativeOptions", void 0);
__decorate([
    property({ type: Boolean })
], KRDateRangePicker.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean })
], KRDateRangePicker.prototype, "invalid", void 0);
__decorate([
    property({ type: String })
], KRDateRangePicker.prototype, "placeholder", void 0);
__decorate([
    property({ type: String })
], KRDateRangePicker.prototype, "startDate", void 0);
__decorate([
    property({ type: String })
], KRDateRangePicker.prototype, "endDate", void 0);
__decorate([
    property({ type: String })
], KRDateRangePicker.prototype, "mode", void 0);
__decorate([
    state()
], KRDateRangePicker.prototype, "_isOpen", void 0);
__decorate([
    state()
], KRDateRangePicker.prototype, "_activeTab", void 0);
__decorate([
    state()
], KRDateRangePicker.prototype, "_tempStartDate", void 0);
__decorate([
    state()
], KRDateRangePicker.prototype, "_tempEndDate", void 0);
// Guarded registration: no-op if the tag is already defined, so a second copy of krubble on
// the page (e.g. the Operations shell loads krubble globally while an embedded app bundled its
// own copy) doesn't throw. First definition wins.
if (!customElements.get('kr-date-range-picker')) {
    customElements.define('kr-date-range-picker', KRDateRangePicker);
}
//# sourceMappingURL=date-range-picker.js.map